import { BoxPage } from '@/components/common/BoxPage'
import { useRouteQuery, useTranslator } from '@/hooks'
import { Card, CardContent, Divider, Typography } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import FilterMonthAttendance from '@/components/attendance/detail-attendance/FillterMonthAttendance'
import { useQuery } from '@tanstack/react-query'
import { Attendances, AttendanceStatusEnum } from '@/@data'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AttendanceEmployeeInformation } from '@/components/attendance/employee/AttendanceEmployeeInformation'
import { AttendanceEmployeePayroll } from '@/components/attendance/employee/AttendanceEmployeePayroll'
import { AttendanceEmployeeTotal } from '@/components/attendance/employee/AttendanceEmployeeTotal'
import { AttendanceEmployeeTable } from '@/components/attendance/tables/AttendanceEmployeeTable'
import { AttendanceEmployeeEditPayroll } from '@/components/attendance/employee/AttendanceEmployeeEditPayroll'
import { AttendanceEmployeeHistory } from '@/components/attendance/employee/AttendanceEmployeeHistory'
import dayjs from 'dayjs'

export const AttendanceEmployeeDetail = () => {
  const { __ } = useTranslator()
  const { searchQuery, updateQuery } = useRouteQuery()
  const { id } = useParams()
  const [isEditPayroll, setIsEditPayroll] = useState(false)
  const [isShowHistory, setIsShowHistory] = useState(false)

  const paramsCompany = useMemo(() => {
    return {
      companyId: searchQuery.get('companyId'),
      month: searchQuery.get('month')
    }
  }, [searchQuery.get('month'), searchQuery.get('companyId')])

  const { data: dataCompany, refetch: refetchCompany, error: errorCompany } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyId(paramsCompany),
    enabled: !!searchQuery.get('companyId') && !!searchQuery.get('month')
  })

  useEffect(() => {
    if (searchQuery.get('is_attendance_detail')) {
      return
    }

    if (dataCompany?.data?.current_pay_period) {
      updateQuery({ month: dataCompany?.data?.current_pay_period })
    }
  }, [dataCompany?.data?.current_pay_period])

  const paramsCompanyBeforeMonth = {
    companyId: searchQuery.get('companyId'),
    month: dayjs(searchQuery.get('month')).subtract(1, 'month').format('YYYY-MM-DD')
  }

  const { data: dataCompanyBeforeMonth, refetch: refetchCompanyBeforeMonth } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyId(paramsCompanyBeforeMonth),
    enabled:
      !!searchQuery.get('companyId') && !!dayjs(searchQuery.get('month')).subtract(1, 'month').format('YYYY-MM-DD')
  })

  // employee
  const paramsEmployee = {
    companyId: searchQuery.get('companyId'),
    userId: id,
    month: searchQuery.get('month')
  }

  const { data: dataEmployee, refetch: refetchEmployee } = useQuery({
    ...Attendances.getAttendanceDetailByCompanyIdEmployee(paramsEmployee),
    enabled: !!id && !!searchQuery.get('month') && !!searchQuery.get('companyId')
  })

  return (
    <>
      {!isShowHistory && (
        <BoxPage
          showBackButton={true}
          title={__('Employee Attendance Detail')}
          breadcrumbs={[
            { label: __('Attendance Management'), href: '/cms/attendance' },
            { label: __('Company Attendance Detail'), href: `/cms/attendance/${searchQuery.get('companyId')}` },
            { label: __('Employee Attendance Detail') }
          ]}
        >
          {!isEditPayroll && (
            <Card className='rounded-[32px]'>
              <CardContent sx={{ padding: 0 }} className='pb-0'>
                <div className='flex justify-between items-center p-5'>
                  <div className='flex items-center gap-2'>
                    <div className='min-w-[200px]'>
                      <Button
                        variant='outlined'
                        size='small'
                        className='border-blue-500 text-blue-600 hover:border-blue-600 w-full'
                      >
                        <span
                          className='text-[15px] font-medium max-w-[500px] whitespace-nowrap overflow-hidden text-ellipsis'
                          title={dataCompany?.data?.company?.name || '-'}
                        >
                          {dataCompany?.data?.company?.name || '-'}
                        </span>
                      </Button>
                    </div>
                    <Divider orientation='vertical' flexItem className='w-[2px] bg-[#2f2b3d1f]' />
                    <Typography variant='h6' className='text-[18px] font-medium'>
                      {__('Defaut Attendance/Payroll')}
                    </Typography>
                  </div>
                </div>
                <Divider />
                <div className='p-5'>
                  <FilterMonthAttendance
                    key={`${searchQuery.get('companyId')}-${dataCompany?.data?.current_pay_period}`}
                    onDateChange={month => {
                      updateQuery({ month })
                    }}
                    status={dataCompany?.data?.status || ''}
                    isShowHistory={true}
                    onShowHistory={() => {
                      setIsShowHistory(true)
                    }}
                    data={dataCompany?.data || {}}
                    isShowWorkingRate={true}
                    isShowSettingDay={true}
                    currentPayPeriod={dataCompany?.data?.current_pay_period}
                    isDisablePreviousMonth={!dataCompanyBeforeMonth}
                  />
                </div>
                <Divider />
                <AttendanceEmployeeInformation dataEmployee={dataEmployee || {}} />
                <Divider />
                <AttendanceEmployeePayroll
                  dataEmployee={dataEmployee || {}}
                  setIsEditPayroll={setIsEditPayroll}
                  isDisabledEditPayroll={dataCompany?.data?.status === AttendanceStatusEnum.Reconciled || !!errorCompany}
                />
                <Divider />
                <AttendanceEmployeeTotal dataEmployee={dataEmployee || {}} />

                <AttendanceEmployeeTable
                  data={dataEmployee?.data?.data || []}
                  minimum_working_days={dataCompany?.data?.minimum_working_days}
                  withdraw_cutoff_day={dataCompany?.data?.company?.withdraw_cutoff_day}
                />
              </CardContent>
            </Card>
          )}
          {isEditPayroll && (
            <AttendanceEmployeeEditPayroll
              dataEmployee={dataEmployee || {}}
              setIsEditPayroll={setIsEditPayroll}
              dataCompany={dataCompany?.data || {}}
              onSuccess={() => {
                refetchEmployee()
                refetchCompany()
                refetchCompanyBeforeMonth()
              }}
            />
          )}
        </BoxPage>
      )}
      {isShowHistory && (
        <AttendanceEmployeeHistory setIsShowHistory={setIsShowHistory} dataCompany={dataCompany?.data || {}} />
      )}
    </>
  )
}
